/**
 * Same shape as every other feature's local `errorMessage()` (see recruitment/errorMessage.ts,
 * employees/AddEmployeeDialog.tsx) — duplicated per module rather than shared, matching this
 * codebase's existing convention.
 *
 * A REQUEST-BODY validation failure (the whole body failed the contract's Zod schema) DOES carry
 * field detail: `apps/api/src/lib/ts-rest.ts` throws `new ValidationError(\`Invalid ${where}\`,
 * result.error.flatten())`, and `AppError.toBody()` includes `details` on any exposed (4xx) error —
 * so the wire body is `{ code: 'VALIDATION_FAILED', message: 'Invalid body', details: { fieldErrors:
 * { title: [...] } } }`. Showing only `message` here would mean every form displays the same
 * unhelpful "Invalid body" for a mistake the server already described precisely. A route-HANDLER
 * validation failure (e.g. the task self-service carve-out's 403) carries a different, non-Zod
 * `details` shape or none at all — `fieldErrors` is checked for structurally rather than assumed.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return "Can't reach the server. Check your connection and try again.";
  }
  const body = (error as { body?: unknown } | undefined)?.body;
  if (!body || typeof body !== 'object') {
    return 'Something went wrong. Please try again.';
  }

  const fieldErrors = fieldErrorsOf(body);
  if (fieldErrors) {
    const parts = Object.entries(fieldErrors)
      .filter(
        (entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].length > 0,
      )
      .map(([field, messages]) => `${field}: ${messages[0]}`);
    if (parts.length > 0) return parts.join('; ');
  }

  if ('message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'Something went wrong. Please try again.';
}

function fieldErrorsOf(body: object): Record<string, unknown> | null {
  const details = (body as { details?: unknown }).details;
  if (!details || typeof details !== 'object') return null;
  const fieldErrors = (details as { fieldErrors?: unknown }).fieldErrors;
  return fieldErrors && typeof fieldErrors === 'object'
    ? (fieldErrors as Record<string, unknown>)
    : null;
}

/**
 * `@ts-rest/react-query` throws the whole typed response for any non-2xx status, so a
 * query/mutation's `error` object carries `status` directly — lets a caller tell a 404 (task
 * outside scope) apart from a 403 (visible, wrong permission) if it ever needs to.
 */
export function getErrorStatus(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
}
